import { useContext } from "react";
import { Box, Grid, MenuItem, Select, TextField } from "@mui/material";
import { useTranslation } from "react-i18next";
import { SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import axios from "axios";

import { AuthContext } from "../../../contexts/AuthContext";
import { CONFIG } from "../../../services/config";

import { FormInputText } from "../../../components/form/FormInputText";
import ActionButtons from "../../../components/MyModal/ActionButtons";
import MyModal from "../../../components/MyModal";

import { COMPLETED, CANCELLED } from "../../../constants";
import formatStatus from "../../../utils/formatStatusName";

type Props = {
  onClose: Function;
  openSucessAlert: Function;
  openErrorAlert: Function;
  refreshPage: Function;
  setActionType: Function;
};

export interface FormValues {
  vin: String;
  serviceLost: String;
  odometer: Number;
  osNumber: Number;
  revisionDate: String;
  status: String;
  dn: String;
}

export default function ModalAddRevalidation({
  onClose,
  openSucessAlert,
  openErrorAlert,
  refreshPage,
  setActionType,
}: Props) {
  const { t } = useTranslation();
  const { tokenHeaders } = useContext(AuthContext);
  const status = [COMPLETED, CANCELLED];

  const formData = yup.object().shape({
    vin: yup.string().label(t("revalidation.add.chassi")),
    odometer: yup.string().required(t("revalidation.add.odometerError")),
    osNumber: yup.string().required(t("revalidation.add.osNumberError")),
    revisionDate: yup.string().required(t("revalidation.add.revisionDate")),
    serviceLost: yup.string().label(t("revalidation.add.lostService")),
    status: yup.string().label(t("revalidation.add.status")),
    dn: yup.string().required(t("revalidation.add.dnCode")),
  });

  const { register, handleSubmit, formState } = useForm<FormValues>({
    resolver: yupResolver(formData),
  });

  const { errors } = formState;

  const HorizontalLine = (
    <Box
      sx={{
        borderTop: "1px solid var(--gray-100)",
        marginBottom: 3,
        marginTop: 2,
      }}
    />
  );

  const addRevalidationService: SubmitHandler<FormValues> = async (values) => {
    try {
      await axios.post(
        `${CONFIG.SOB_API}revalidate-plan`,
        {
          vin: values.vin,
          odometer: values.odometer,
          osNumber: values.osNumber,
          revisionDate: values.revisionDate,
          serviceLost: values.serviceLost,
          status: values.status,
          dn: values.dn,
        },
        tokenHeaders,
      );
      onClose();
      refreshPage();
      openSucessAlert();
      setActionType("add");
    } catch (error: any) {
      onClose();
      openErrorAlert();
    }
  };

  return (
    <MyModal
      title={t("revalidation.add.titleModal")}
      modalChange={onClose}
      component="form"
      onSubmit={handleSubmit(addRevalidationService)}
    >
      <Grid container spacing={2}>
        <Grid item md={3} xs={12}>
          <span>{t("revalidation.add.vehicle")}</span>
        </Grid>
        <Grid item md={5} xs={12}>
          <FormInputText
            id="vin"
            label={t("revalidation.add.chassi")}
            {...register("vin")}
          />
        </Grid>
        <Grid item md={4} xs={12}>
          <FormInputText
            id="odometer"
            type="number"
            label={t("revalidation.add.odometer")}
            inputProps={{ max: 999999 }}
            error={errors.odometer}
            {...register("odometer")}
          />
        </Grid>
      </Grid>

      {HorizontalLine}

      <Grid container spacing={2}>
        <Grid item md={3} xs={12}>
          <span>{t("revalidation.add.service")}</span>
        </Grid>
        <Grid item md={5} xs={12}>
          <FormInputText
            id="osNumber"
            type="text"
            label={t("revalidation.add.osNumber")}
            inputProps={{
              maxLength: 10,
              minLength: 1,
            }}
            error={errors.osNumber}
            {...register("osNumber")}
          />
        </Grid>
        <Grid item md={4} xs={12}>
          <FormInputText
            id="revisionDate"
            type="date"
            label={t("revalidation.add.revisionDate")}
            InputProps={{
              inputProps: { min: "1900-01-01", max: "2100-01-01" },
            }}
            error={errors.revisionDate}
            {...register("revisionDate")}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item md={3} xs={12} />
        <Grid item md={5} xs={12}>
          <FormInputText
            id="serviceLost"
            label={t("revalidation.add.lostService")}
            {...register("serviceLost")}
          />
        </Grid>
        <Grid item md={4} xs={12} style={{ height: "10px" }}>
          <FormInputText
            select
            id="status"
            label={t("revalidation.add.status")}
            {...register("status")}
          >
            {status.map((status) => (
              <MenuItem key={status} value={status}>
                {formatStatus(status)}
              </MenuItem>
            ))}
          </FormInputText>
        </Grid>
      </Grid>

      {HorizontalLine}

      <Grid container spacing={2}>
        <Grid item md={3} xs={12}>
          <span>{t("revalidation.add.dealer")}</span>
        </Grid>
        <Grid item md={5} xs={12}>
          <FormInputText
            id="dn"
            type="number"
            label={t("revalidation.add.dnCode")}
            inputProps={{ max: 99999999999 }}
            error={errors.dn}
            {...register("dn")}
          />
        </Grid>
      </Grid>

      {HorizontalLine}

      <p style={{ fontSize: 12, marginBottom: 30 }}>
        {t("revalidation.add.requiredFields")}
      </p>

      <ActionButtons
        typeButton="submit"
        saveName={t("button.Save")}
        enableLine={false}
      />
    </MyModal>
  );
}
